import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TaptapList } from 'sonata-react-components';
import { formatNumber, formatPercentage } from 'sonata-static-storage/lib/helpers/generalFormatters.helpers';

import { formatMoney } from '@/components/driveToStore/common/helpers/formatters';
import { getFootfallStoreDetail } from '@/services/footfall.service';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';
import BestDayTag from '../../AggregatedReport/components/BestDayTag/BestDayTag';

import type { MetricFilters, MetricsUnitMode } from '@/services/footfall.service';

import './poiStoreTable.scss';

type PoiStoreTableItem = {
  id: string;
  name: string;
  address?: string | null;
  brand?: string | null;
  upliftPercentage?: number | null;
  uplift?: number | null;
  baseline?: number | null;
  costPerVisit?: number | null;
  bestDay?: string | null;
};

type FetchParams = {
  entityType: string;
  entityId: string;
  userId: string;
  country?: string;
  venueIds?: string[];
  nameFilter?: string;
  tagFilters?: Record<string, string[]>;
  metricFilters?: MetricFilters;
  metricsUnitMode?: MetricsUnitMode;
  brandFilter?: string[];
};

type Props = {
  id: string;
  showBestDay?: boolean;
  selectedIds?: Set<string>;
  onToggle?: (id: string) => void;
  initialPageSize?: number;
  listToolbar?: JSX.Element;
  height?: number;
  fetchParams: FetchParams;
};

const columnToField: Record<string, string> = {
  baseline: 'fa_venue_baseline',
  cpv: 'fa_venue_cost_per_visit',
  uplift: 'fa_venue_uplift',
  upliftPct: 'fa_venue_uplift_pct'
};

function formatNumberValue(value: number | null | undefined, country: string, withSign = false): string {
  if (value == null) {
    return '—';
  }
  const formatted = formatNumber(value, { country, fixed: 0 });
  return withSign && value > 0 ? `+${formatted}` : formatted;
}

function formatPercentValue(value: number | null | undefined, country: string, withSign = false): string {
  if (value == null) {
    return '—';
  }
  const formatted = formatPercentage(value, { base: 100, country, fixedDecimals: 2 });
  return withSign && value > 0 ? `+${formatted}` : formatted;
}

export function PoiStoreTable({
  id,
  showBestDay = true,
  selectedIds,
  onToggle,
  initialPageSize = 10,
  listToolbar,
  height = 480,
  fetchParams: {
    entityType,
    entityId,
    userId,
    country,
    venueIds,
    nameFilter,
    tagFilters,
    metricFilters,
    metricsUnitMode,
    brandFilter
  }
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const baseCurrency = useGlobalStore(getBaseCurrency);

  const gridModifier = showBestDay ? 'poiStoreTable--withBestDay' : 'poiStoreTable--noBestDay';
  const formatCountry = baseCurrency?.countries?.[0] || 'default';

  const defaultSelectedRows = useMemo(() => {
    return selectedIds ? Array.from(selectedIds).map(i => ({ id: i })) : [];
  }, [selectedIds]) as PoiStoreTableItem[];

  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);

  const fetchData = useCallback(
    async (params: { page: number; pageSize: number }) => {
      try {
        const response = await getFootfallStoreDetail(entityType, entityId, userId, {
          country: country,
          itemsPerPage: params.pageSize,
          ...(nameFilter ? { nameFilter: nameFilter } : {}),
          ...(tagFilters && Object.keys(tagFilters).length ? { tagFilters } : {}),
          ...(metricFilters && Object.keys(metricFilters).length ? { metricFilters, metricsUnitMode } : {}),
          ...(brandFilter && brandFilter.length ? { brandFilter } : {}),
          page: params.page,
          ...(sortBy ? { sortBy, sortOrder } : {}),
          venueIds: venueIds
        });

        const items: PoiStoreTableItem[] =
          response.items?.map(row => ({
            address: row.address,
            baseline: row.baseline,
            bestDay: row.bestDay,
            brand: row.brand,
            costPerVisit: row.costPerVisit,
            id: row.venueId,
            name: row.storeName,
            uplift: row.uplift,
            upliftPercentage: row.upliftPercentage
          })) ?? [];

        return {
          data: items,
          page: params.page,
          pageSize: params.pageSize,
          total: response.totalItems,
          totalPages: Math.ceil(response.totalItems / params.pageSize)
        };
      } catch {
        return { data: [], page: params.page, pageSize: params.pageSize, total: 0, totalPages: 0 };
      }
    },
    [
      entityType,
      entityId,
      userId,
      country,
      venueIds,
      nameFilter,
      tagFilters,
      metricFilters,
      metricsUnitMode,
      brandFilter,
      sortBy,
      sortOrder
    ]
  );

  return (
    <div className={cn('poiStoreTable', gridModifier)} style={{ height }}>
      <TaptapList<PoiStoreTableItem>
        className="poiStoreTable__list"
        defaultSelectedItems={defaultSelectedRows}
        fetchData={fetchData}
        id={id}
        initialPageSize={initialPageSize}
        onSortChange={(columnId, direction) => {
          const field = columnToField[columnId];
          if (field) {
            setSortBy(field);
            setSortOrder(direction);
          }
        }}
      >
        {listToolbar ? listToolbar : null}
        <TaptapList.ColumnsBar>
          <TaptapList.ColumnsBar.Column
            className="poiStoreTable__colStore"
            description={formatMessage({
              defaultMessage: 'Store',
              id: 'driveToStore.aggregatedReport.storeDetail.column.store'
            })}
            id="store"
          />
          <TaptapList.ColumnsBar.Column
            className="poiStoreTable__colUpliftPct"
            description={formatMessage({
              defaultMessage: 'Uplift %',
              id: 'driveToStore.aggregatedReport.storeDetail.column.upliftPct'
            })}
            id="upliftPct"
            sortable
          />
          <TaptapList.ColumnsBar.Column
            className="poiStoreTable__colUplift"
            description={formatMessage({
              defaultMessage: 'Uplift',
              id: 'driveToStore.aggregatedReport.storeDetail.column.uplift'
            })}
            id="uplift"
            sortable
          />
          <TaptapList.ColumnsBar.Column
            className="poiStoreTable__colBaseline"
            description={formatMessage({
              defaultMessage: 'Baseline',
              id: 'driveToStore.aggregatedReport.storeDetail.column.baseline'
            })}
            id="baseline"
            sortable
          />
          <TaptapList.ColumnsBar.Column
            className="poiStoreTable__colCpv"
            description={formatMessage({
              defaultMessage: 'CPSV',
              id: 'driveToStore.aggregatedReport.storeDetail.column.cpv'
            })}
            id="cpv"
            sortable
          />
          {showBestDay && (
            <TaptapList.ColumnsBar.Column
              className="poiStoreTable__colBestDay"
              description={formatMessage({
                defaultMessage: 'Best Day',
                id: 'driveToStore.aggregatedReport.storeDetail.column.bestDay'
              })}
              id="bestDay"
            />
          )}
        </TaptapList.ColumnsBar>
        <TaptapList.RowsList<PoiStoreTableItem>
          emptyStateIcon="fal fa-empty-set"
          emptyStateTitle={formatMessage({
            defaultMessage: 'There is no data for the current selection.',
            id: 'driveToStore.aggregatedReport.storeDetail.emptyState.title'
          })}
        >
          {itemsList =>
            itemsList.map(item => (
              <TaptapList.RowsList.Row id={item.id} key={item.id}>
                <div className="poiStoreTable__row" onClick={() => onToggle?.(item.id)}>
                  <div className="poiStoreTable__rowTitle">
                    <span className="poiStoreTable__storeName" title={item.name}>
                      {item.name}
                    </span>
                  </div>
                  <div className="poiStoreTable__rowSubtitle">
                    <span className="poiStoreTable__storeAddress" title={item.address ?? undefined}>
                      {item.address ?? ' '}
                    </span>
                  </div>
                  <span
                    className={cn('poiStoreTable__colUpliftPct', 'poiStoreTable__upliftPct', {
                      'poiStoreTable__upliftPct--positive': item.upliftPercentage != null && item.upliftPercentage > 0
                    })}
                  >
                    {formatPercentValue(item.upliftPercentage, formatCountry, true)}
                  </span>
                  <span
                    className={cn('poiStoreTable__colUplift', 'poiStoreTable__uplift', {
                      'poiStoreTable__uplift--positive': item.uplift != null && item.uplift > 0
                    })}
                  >
                    {formatNumberValue(item.uplift, formatCountry, true)}
                  </span>
                  <span className="poiStoreTable__colBaseline poiStoreTable__baseline">
                    {formatNumberValue(item.baseline, formatCountry)}
                  </span>
                  <span className="poiStoreTable__colCpv poiStoreTable__cpv">
                    {formatMoney(item.costPerVisit, baseCurrency)}
                  </span>
                  {showBestDay && (
                    <span className="poiStoreTable__colBestDay poiStoreTable__bestDay">
                      <BestDayTag id={`poiStoreTable-bestDay-${item.id}`} value={item.bestDay} />
                    </span>
                  )}
                </div>
              </TaptapList.RowsList.Row>
            ))
          }
        </TaptapList.RowsList>
      </TaptapList>
    </div>
  );
}
